// Block-type registry — one source of truth for both the public renderer
// AND the admin editor. Each type defines:
//   • label            → human label in the "Add a Block" menu
//   • icon             → lucide icon component reference (JSX not allowed here)
//   • defaultConfig    → seed config for a newly-added block
//   • fields           → schema-driven admin form (list of {key, label, kind, options?})
//   • Render(props)    → renders the block on the public page
//                         props: {block, festival?, ctx}  where ctx carries
//                         data like {isAdmin, layoutKey, refresh}
//
// New types = one entry here. Keeping the registry tight prevents drift
// between the editor UI and the renderer.

import HeroBlock from "./HeroBlock";
import NorthStarGuideBlock from "./NorthStarGuideBlock";
import { FeaturedArtistsBlock, SuggestChangesBlock } from "./OnScreenBlocks";
import RichTextBlock from "./RichTextBlock";
import MapBlock from "./MapBlock";
import DontMissGridBlock from "./DontMissGridBlock";
import ScheduleBlock from "./ScheduleBlock";
import ProgramBlock from "./ProgramBlock";
import TrovePlaylistBlock from "./TrovePlaylistBlock";
import FeaturedBadgeBlock from "./FeaturedBadgeBlock";
import PhotoGalleryBlock from "./PhotoGalleryBlock";
import CtaBlock from "./CtaBlock";
import SponsorsBlock from "./SponsorsBlock";
import NavCardEmbedBlock from "./NavCardEmbedBlock";
import AnnouncementTickerBlock from "./AnnouncementTickerBlock";
import NorthStarBlock from "./NorthStarBlock";
import TourStopsBlock from "./TourStopsBlock";
import PosterWallBlock from "./PosterWallBlock";
import FeatureCardsBlock from "./FeatureCardsBlock";
import TimelineBlock from "./TimelineBlock";
import VideoBlock from "./VideoBlock";
import StoryChapterBlock from "./StoryChapterBlock";
import AudioBlock from "./AudioBlock";
import DocumentBlock from "./DocumentBlock";
import ReleaseSpotlightBlock from "./ReleaseSpotlightBlock";
import LinkHubBlock from "./LinkHubBlock";
import GuideCardBlock from "./GuideCardBlock";
import SafetyPlanBlock from "./SafetyPlanBlock";
import TourCardBlock from "./TourCardBlock";
import EpkBlock from "./EpkBlock";
import ArtistShowsBlock from "./ArtistShowsBlock";
import BusinessGemsBlock from "./BusinessGemsBlock";
import { HOME_BLOCK_TYPES } from "../home/homeBlocks";

// `kind` values used in the admin form:
//   text | textarea | number | boolean | color | route | image | image-list |
//   coords | schedule-editor | link-list | csv-schedule | select
//
// A field with `dependsOn: {key, equals}` only shows when that other field
// matches — used e.g. to reveal CTA fields only when the block has one.

export const BLOCK_REGISTRY = {
  business_gems: {
    label: "Business Gems — this business's Gems, the Gem rules, redeem + highlights",
    hint: "Pulls the business's Gems from THE HOARD (hidden from visitors until the HOARD opens). Part of every standard business page.",
    icon: "Gem",
    defaultConfig: { business_id: "", anchor: "gems", show_highlights: true, accent: "#B8860B" },
    fields: [
      { key: "business_id", label: "Business ID (biz_…)", kind: "text" },
      { key: "anchor", label: "Link anchor (e.g. gems → #gems)", kind: "text" },
      { key: "show_highlights", label: "Show the business's highlights under the Gems", kind: "boolean" },
      { key: "accent", label: "Accent color", kind: "color" },
    ],
    Render: BusinessGemsBlock,
  },
  epk: {
    label: "EPK — Electronic Press Kit one-sheet (artists)",
    hint: "What bookers scan in 90 seconds. Title = act name, Quip = one-line pitch. Videos/photos/proof/members/socials are one per line; socials as 'Label | https://…'.",
    icon: "FileText",
    defaultConfig: { anchor: "epk", accent: "#B8860B", genre: "", hometown: "", award: "", short_bio: "", long_bio: "", members: "", videos: "", music_embed: "", music_embed_height: 320, photos: "", proof: "", booking_name: "", booking_email: "", booking_phone: "", set_lengths: "", formats: "", stage_plot_url: "", tech_rider_url: "", socials: "", merch_url: "", epk_url: "" },
    fields: [
      { key: "genre", label: "Genre", kind: "text" }, { key: "hometown", label: "Hometown", kind: "text" }, { key: "award", label: "Award / headline credential", kind: "text" },
      { key: "short_bio", label: "Short bio (50–100 words, third person)", kind: "textarea", rows: 3 }, { key: "long_bio", label: "Long bio (200–400 words)", kind: "textarea", rows: 8 },
      { key: "members", label: "Line-up — one per line (Name — instrument)", kind: "textarea", rows: 4 },
      { key: "videos", label: "Live video URLs (YouTube/Vimeo) — one per line, best first, max 5", kind: "textarea", rows: 5 },
      { key: "music_embed", label: "Music embed URL (Bandcamp/Spotify/SoundCloud embed src)", kind: "text" }, { key: "music_embed_height", label: "Music embed height (px)", kind: "number" },
      { key: "photos", label: "Press photo URLs — one per line (hi-res)", kind: "textarea", rows: 4 },
      { key: "proof", label: "Proof — venues, festivals, press quotes, awards — one per line", kind: "textarea", rows: 5 },
      { key: "booking_name", label: "Booking contact name", kind: "text" }, { key: "booking_email", label: "Booking email", kind: "text" }, { key: "booking_phone", label: "Booking phone", kind: "text" },
      { key: "set_lengths", label: "Set lengths (e.g. 45 / 90 / 3 hrs)", kind: "text" }, { key: "formats", label: "Formats (solo acoustic, duo, full band…)", kind: "text" },
      { key: "stage_plot_url", label: "Stage plot URL (PDF/image)", kind: "text" }, { key: "tech_rider_url", label: "Tech / hospitality rider URL", kind: "text" },
      { key: "socials", label: "Links — one per line as 'Label | URL'", kind: "textarea", rows: 5 }, { key: "merch_url", label: "Merch URL", kind: "text" }, { key: "epk_url", label: "External EPK site URL", kind: "text" },
      { key: "accent", label: "Accent color", kind: "color" }, { key: "anchor", label: "Anchor id", kind: "text" },
    ],
    Render: EpkBlock,
  },
  tour_card: {
    label: "Welcome Tour card — one 'door' in the home-page tour",
    hint: "Used by the Welcome Tour panel (home:welcome-tour). Title = headline, Quip = one-liner on the card. Body + bullets show in the expanded view. Routes can end with #anchor (e.g. /#happening-map).",
    icon: "DoorOpen",
    defaultConfig: { eyebrow: "", image: "", accent: "#F2CC8F", body: "", bullets: "", cta_label: "Learn more", cta_route: "/", secondary_label: "", secondary_route: "" },
    fields: [
      { key: "eyebrow", label: "Small line above the headline (e.g. For businesses)", kind: "text" },
      { key: "image", label: "Card image (square works best)", kind: "image" },
      { key: "accent", label: "Accent color", kind: "color" },
      { key: "body", label: "Expanded view — paragraph", kind: "textarea", rows: 3 },
      { key: "bullets", label: "Expanded view — benefits, one per line", kind: "textarea", rows: 5 },
      { key: "cta_label", label: "Main button label", kind: "text" },
      { key: "cta_route", label: "Main button route (/for-bands, /#happening-map, https://…)", kind: "text" },
      { key: "secondary_label", label: "Second button label (optional)", kind: "text" },
      { key: "secondary_route", label: "Second button route", kind: "text" },
    ],
    Render: TourCardBlock,
  },
  hero: {
    label: "Hero — Title + Motto + Image",
    hint: "Big top-of-page banner. Auto-added when a festival is created.",
    icon: "Star",
    defaultConfig: { hero_image: "", start_date: "", end_date: "", background: "#0A1626", button_label: "", button_url: "", button_color: "#F2CC8F", button2_label: "", button2_url: "", show_share: false },
    fields: [
      { key: "hero_image", label: "Hero image", kind: "image" },
      { key: "logo", label: "Logo shown whole above the title (businesses/orgs) — background stays a photo or color", kind: "image" },
      { key: "logo_style", label: "Logo size (big = full-width tile · badge = small square)", kind: "select", options: ["big", "badge"] },
      { key: "banner_image", label: "Second image shown above the logo (optional)", kind: "image" },
      { key: "title_first", label: "Show the title above the images (instead of below)", kind: "boolean" },
      { key: "logo_below_text", label: "Show the logo below the intro text (above the buttons)", kind: "boolean" },
      { key: "eyebrow", label: "Small label above the title (default: Festival)", kind: "text" },
      { key: "start_date", label: "Start date (YYYY-MM-DD)", kind: "text" },
      { key: "end_date", label: "End date (YYYY-MM-DD)", kind: "text" },
      { key: "background", label: "Background color", kind: "color" },
      { key: "button_label", label: "Button label (e.g. Official website)", kind: "text" },
      { key: "button_url", label: "Button destination (URL or path)", kind: "route" },
      { key: "button_color", label: "Button color", kind: "color" },
      { key: "button2_label", label: "Second button label (optional)", kind: "text" },
      { key: "button2_url", label: "Second button destination", kind: "route" },
      { key: "show_share", label: "Show a \"Share this page\" button", kind: "boolean" },
      { key: "socials", label: "Social / listen pills under the buttons", kind: "link-list", itemFields: [
        { key: "label", label: "Label", kind: "text" }, { key: "url", label: "URL", kind: "text" },
        { key: "icon", label: "Lucide icon (e.g. Instagram, Facebook, Disc3, Headphones)", kind: "text" }, { key: "color", label: "Icon color", kind: "color" },
      ] },
      { key: "facts", label: "Facts strip under the intro (hours, address, phone…)", kind: "link-list", itemFields: [
        { key: "label", label: "Small label (e.g. Hours)", kind: "text" }, { key: "value", label: "Value (e.g. Mon–Sat 7–3)", kind: "text" },
        { key: "url", label: "Link (optional — #anchor, tel:, or URL)", kind: "text" }, { key: "icon", label: "Lucide icon (Clock, MapPin, Phone, Gem)", kind: "text" },
      ] },
      { key: "section_links", label: "Section jump chips (strip under the splash)", kind: "link-list", itemFields: [
        { key: "label", label: "Label", kind: "text" }, { key: "url", label: "Anchor or path (e.g. #discography)", kind: "text" },
      ] },
    ],
    Render: HeroBlock,
  },
  rich_text: {
    label: "Rich Text — Title, Motto, Body (optional map/gallery)",
    hint: "Your all-purpose custom node. Add a paragraph, a mini map, a gallery — pick what shows.",
    icon: "FileText",
    defaultConfig: {
      body: "",
      show_map: false,
      map_center: null,
      map_zoom: 15,
      show_gallery: false,
      images: [],
      accent: "#2E5C55",
    },
    fields: [
      { key: "body", label: "Body copy", kind: "textarea", rows: 5 },
      { key: "accent", label: "Accent color", kind: "color" },
      { key: "show_map", label: "Show inline map", kind: "boolean" },
      { key: "map_center", label: "Map center [lat, lng]", kind: "coords", dependsOn: { key: "show_map", equals: true } },
      { key: "map_zoom", label: "Map zoom (1-20)", kind: "number", dependsOn: { key: "show_map", equals: true } },
      { key: "show_gallery", label: "Show photo gallery", kind: "boolean" },
      { key: "images", label: "Gallery images", kind: "image-list", dependsOn: { key: "show_gallery", equals: true } },
    ],
    Render: RichTextBlock,
  },
  map: {
    label: "Map — Interactive Porch/Venue Map",
    hint: "Auto-plots Don't Miss spots tagged to this festival. You can add multiple maps.",
    icon: "MapPin",
    defaultConfig: {
      center: null,
      zoom: 15,
      height: 520,
      festival_filter: true,
      include_dont_miss: true,
      custom_pins: [],
    },
    fields: [
      { key: "center", label: "Map center [lat, lng]", kind: "coords" },
      { key: "zoom", label: "Zoom (1-20)", kind: "number" },
      { key: "height", label: "Height (px)", kind: "number" },
      { key: "include_dont_miss", label: "Show Don't Miss pins", kind: "boolean" },
      { key: "show_gems", label: "Show Gem businesses (🎁 pins for every business with a live offer)", kind: "boolean" },
      { key: "giving_badge", label: "Show the Stars 4 Peace Giving Map badge (top-left)", kind: "boolean" },
      { key: "giving_badge_sub", label: "Giving badge subtitle (e.g. Seasonal Needs & Toys Drive)", kind: "text" },
      { key: "custom_pins", label: "Extra pins", kind: "link-list", itemFields: [
        { key: "name", label: "Name", kind: "text" },
        { key: "lat", label: "Lat", kind: "number" },
        { key: "lng", label: "Lng", kind: "number" },
        { key: "note", label: "Popup note", kind: "text" },
      ]},
    ],
    Render: MapBlock,
  },
  dontmiss_grid: {
    label: "Don't Miss Grid — Featured Pop-ups",
    hint: "Renders every Don't Miss spot tagged to this festival with logos, photos, and links.",
    icon: "Sparkles",
    defaultConfig: { festival_slug: "" },
    fields: [
      { key: "festival_slug", label: "Festival slug (leave blank to auto-use this festival)", kind: "text" },
    ],
    Render: DontMissGridBlock,
  },
  schedule: {
    label: "Schedule — Days & Acts (CSV or row-by-row)",
    hint: "Upload a CSV or JSON, or add rows one at a time. Each day gets its own collapsible section.",
    icon: "CalendarDays",
    defaultConfig: { days: [] },
    fields: [
      { key: "days", label: "Days", kind: "schedule-editor" },
      { key: "csv_upload", label: "Bulk upload (CSV or JSON)", kind: "csv-schedule" },
    ],
    Render: ScheduleBlock,
  },
  featured_badge: {
    label: "Featured Badge — dark event card (image + title + headline) with a sponsor/partner strip",
    hint: "The compact home-page badge. Title = event name. Headline = the bold one-liner. The strip underneath is for a sponsor, partner or a nod to what's next.",
    icon: "BadgeCheck",
    defaultConfig: { eyebrow: "", headline: "", image: "", image_shape: "round", link: "", accent: "#F2CC8F", sponsor_image: "", sponsor_text: "", sponsor_link: "" },
    fields: [
      { key: "theme", label: "Card theme (dark navy · light = white card, black text)", kind: "select", options: ["dark", "light"] },
      { key: "title_smallcaps", label: "Title small-caps: lowercase letters render as smaller capitals (HOliday HOpe HOp)", kind: "boolean" },
      { key: "eyebrow", label: "Small gold line above the title (e.g. Gig Harbor · Sept 18–20 · Sunday is the final day)", kind: "text" },
      { key: "headline", label: "Bold one-liner under the title", kind: "textarea", rows: 2 },
      { key: "image", label: "Event image / logo", kind: "image" },
      { key: "image_shape", label: "Image shape", kind: "select", options: ["round", "square", "wide"] },
      { key: "image_focus", label: "Image focus (e.g. center 30%)", kind: "text" },
      { key: "link", label: "Where the card goes (route like /gather or full URL)", kind: "text" },
      { key: "button_label", label: "Button label (optional pill button, e.g. Details here — goes where the card goes)", kind: "text" },
      { key: "show_from", label: "Show from (optional — hidden before this date/time)", kind: "datetime" },
      { key: "show_until", label: "Show until (optional — card disappears after this date/time)", kind: "datetime" },
      { key: "accent", label: "Accent / border color", kind: "color" },
      { key: "sponsor_image", label: "Strip — logo or picture", kind: "image" },
      { key: "sponsor_image_style", label: "Strip image style (logo = white tile · photo = rounded picture)", kind: "select", options: ["logo", "photo"] },
      { key: "sponsor_text", label: "Strip — text (links & **bold** allowed)", kind: "textarea", rows: 2 },
      { key: "sponsor_link", label: "Strip — link (route or URL)", kind: "text" },
    ],
    Render: FeaturedBadgeBlock,
  },
  trove_playlist: {
    label: "HOARD Playlist — An artist's tracks with a stay-on-screen player",
    hint: "Pulls the artist's approved music from THE HOARD by artist slug. Visitors press play and the bar stays pinned to the bottom while they scroll.",
    icon: "ListMusic",
    defaultConfig: { artist_slug: "", intro: "", accent: "#B8860B", visible_rows: 6, limit: 40, trove_link: "/trove", trove_link_label: "" },
    fields: [
      { key: "artist_slug", label: "Artist slug (as in THE HOARD / featured artists, e.g. dain-norman)", kind: "text" },
      { key: "intro", label: "Intro line ([text](url) for links)", kind: "textarea", rows: 2 },
      { key: "anchor", label: "Link anchor (e.g. listen → #listen)", kind: "text" },
      { key: "accent", label: "Accent color", kind: "color" },
      { key: "cover", label: "Fallback cover (when a track has no album art)", kind: "image" },
      { key: "visible_rows", label: "Rows shown before 'Show all'", kind: "number" },
      { key: "trove_link", label: "Link under the list (route or URL)", kind: "text" },
      { key: "trove_link_label", label: "Link label", kind: "text" },
      { key: "now_playing_label", label: "Now-playing bar label", kind: "text" },
    ],
    Render: TrovePlaylistBlock,
  },
  program: {
    label: "Program — Timed run-of-show with jump links, live 'happening now' & embeds",
    hint: "One row per moment. Time is 24-hour HH:MM (e.g. 13:15). Rows sharing the same 'Part' become one jump-to section. Paste a YouTube link or an Instagram post/reel URL on a row to embed it under that moment. On the event day the current moment is highlighted automatically.",
    icon: "ListOrdered",
    defaultConfig: { intro: "", accent: "#1d4ed8", event_date: "", end_time: "", timezone: "America/Los_Angeles", items: [], footnote: "" },
    fields: [
      { key: "intro", label: "Intro line ([text](url) for links)", kind: "textarea", rows: 2 },
      { key: "anchor", label: "Link anchor (e.g. program → #program)", kind: "text" },
      { key: "accent", label: "Accent color", kind: "color" },
      { key: "event_date", label: "Event date (YYYY-MM-DD) — enables the live marker", kind: "text" },
      { key: "end_time", label: "Event end time (HH:MM, 24h)", kind: "text" },
      { key: "items", label: "Moments", kind: "link-list", itemFields: [
        { key: "part", label: "Part (section heading, e.g. Morning program on stage)", kind: "text" },
        { key: "time", label: "Time (HH:MM, 24-hour)", kind: "text" },
        { key: "title", label: "Title (what happens)", kind: "text" },
        { key: "who", label: "Who (performers / speaker)", kind: "text" },
        { key: "kind", label: "Kind", kind: "select", options: ["welcome", "ceremony", "music", "speaker", "keynote", "dance", "poem", "memoriam", "ritual", "youth", "parade", "break", "info"] },
        { key: "note", label: "Note (optional; links allowed)", kind: "textarea" },
        { key: "image", label: "Photo (optional)", kind: "image" },
        { key: "embed_url", label: "YouTube / Vimeo link to embed (optional)", kind: "text" },
        { key: "instagram_url", label: "Instagram post or reel URL to embed (optional)", kind: "text" },
        { key: "embed_caption", label: "Caption under the embed (optional)", kind: "textarea" },
        { key: "link", label: "Link (optional)", kind: "text" },
        { key: "link_label", label: "Link label", kind: "text" },
      ] },
      { key: "footnote", label: "Footnote (e.g. 'Times approximate')", kind: "textarea", rows: 2 },
    ],
    Render: ProgramBlock,
  },
  photo_gallery: {
    label: "Photo Gallery — Drag-to-reorder grid",
    hint: "Simple photo grid. First image is the lead.",
    icon: "Images",
    defaultConfig: { images: [], columns: 3 },
    fields: [
      { key: "columns", label: "Columns (2, 3, or 4)", kind: "number" },
      { key: "fit", label: "Fit", kind: "select", options: ["square", "natural"] },
      { key: "images", label: "Photos", kind: "image-list" },
    ],
    Render: PhotoGalleryBlock,
  },
  video: {
    label: "Video — Upload a clip + Share/Download buttons",
    hint: "Editors upload straight from a phone (MP4/MOV/WebM, up to 200MB) using the button on the page. Visitors get Share + Download for socials.",
    icon: "Video",
    defaultConfig: { video_url: "", poster: "", caption: "", intro: "" },
    fields: [
      { key: "intro", label: "Intro line (optional)", kind: "text" },
      { key: "caption", label: "Caption under the video", kind: "textarea", rows: 2 },
      { key: "poster", label: "Cover image (optional)", kind: "image" },
      { key: "video_url", label: "Video URL (filled automatically by the upload button on the page)", kind: "text" },
      { key: "embed_url", label: "Embed — paste a YouTube / Vimeo link or the full <iframe> embed code (used when no upload)", kind: "textarea", rows: 2 },
      { key: "show_coming_soon", label: "Show a public \"Content coming soon\" card until there is a video", kind: "boolean" },
      { key: "coming_soon_note", label: "Coming-soon note (optional)", kind: "text", dependsOn: { key: "show_coming_soon", equals: true } },
      { key: "source_link", label: "Link to the original (default: stars4peace.org/origins)", kind: "text", dependsOn: { key: "show_coming_soon", equals: true } },
    ],
    Render: VideoBlock,
  },
  audio: {
    label: "Audio — Upload a recording + Share/Download",
    hint: "Editors upload MP3/M4A/WAV (up to 40MB) from the page. Hidden to visitors until there is audio.",
    icon: "Music",
    defaultConfig: { audio_url: "", track_title: "", caption: "", intro: "", cover: "" },
    fields: [
      { key: "track_title", label: "Track title", kind: "text" },
      { key: "intro", label: "Intro line (optional)", kind: "text" },
      { key: "caption", label: "Caption under the player", kind: "textarea", rows: 2 },
      { key: "cover", label: "Cover image (optional)", kind: "image" },
      { key: "audio_url", label: "Audio URL (filled automatically by the upload button on the page)", kind: "text" },
      { key: "embed_url", label: "Embed — paste a SoundCloud / Bandcamp / YouTube link or the full <iframe> embed code (used when no upload)", kind: "textarea", rows: 2 },
      { key: "embed_height", label: "Embed height in px (default 166)", kind: "number" },
      { key: "show_coming_soon", label: "Show a public \"Content coming soon\" card until there is audio", kind: "boolean" },
      { key: "coming_soon_note", label: "Coming-soon note (optional)", kind: "text", dependsOn: { key: "show_coming_soon", equals: true } },
      { key: "source_link", label: "Link to the original (default: stars4peace.org/origins)", kind: "text", dependsOn: { key: "show_coming_soon", equals: true } },
    ],
    Render: AudioBlock,
  },
  document: {
    label: "Document — PDF + page screenshots",
    hint: "For an original publication (e.g. a PDF). Editors upload the PDF from the page; add screenshots of its pages here. Can show a public 'Content coming soon' card until the file is up.",
    icon: "FileText",
    defaultConfig: { intro: "", file_label: "", caption: "", file_url: "", show_preview: false, screenshots: [], show_coming_soon: true, coming_soon_note: "", source_link: "" },
    fields: [
      { key: "intro", label: "Intro paragraph (optional)", kind: "textarea", rows: 3 },
      { key: "file_label", label: "File label (e.g. Tacoma's Outsider's Guide — original 2011 PDF)", kind: "text" },
      { key: "caption", label: "Caption under the label", kind: "textarea", rows: 2 },
      { key: "show_preview", label: "Show the PDF inline (desktop only)", kind: "boolean" },
      { key: "screenshots", label: "Screenshots of the pages", kind: "image-list" },
      { key: "file_url", label: "PDF URL (filled automatically by the upload button on the page)", kind: "text" },
      { key: "show_coming_soon", label: "Show a public \"Content coming soon\" card until there is a file", kind: "boolean" },
      { key: "coming_soon_note", label: "Coming-soon note (optional)", kind: "text", dependsOn: { key: "show_coming_soon", equals: true } },
      { key: "source_link", label: "Link to see it elsewhere meanwhile (URL or path)", kind: "text", dependsOn: { key: "show_coming_soon", equals: true } },
      { key: "source_label", label: "Label for that link", kind: "text", dependsOn: { key: "show_coming_soon", equals: true } },
    ],
    Render: DocumentBlock,
  },
  release_spotlight: {
    label: "Release Spotlight — Single / album: cover, player, buy + share",
    hint: "The centerpiece for a music release. Paste the Bandcamp / Spotify / SoundCloud <iframe> embed code, add the buy link, platform pills and an optional 'where the money goes' note.",
    icon: "Disc3",
    defaultConfig: { eyebrow: "New single", cover: "", title: "", artist: "", release_date: "", format: "", dedication: "", blurb: "", embed_url: "", embed_height: 120, buy_url: "", buy_label: "Buy / stream on Bandcamp", links: [], cause_title: "", cause_note: "", show_share: true, accent: "#F2CC8F", background: "#0A1626" },
    fields: [
      { key: "compact_default", label: "Start compact: dedication + player first, details collapsed", kind: "boolean" },
      { key: "more_label", label: "Expand button label", kind: "text" },
      { key: "release_at", label: "Release moment (ISO, e.g. 2026-09-19T11:20:00+00:00) — card flips to 'Out now' automatically", kind: "text" },
      { key: "out_now_eyebrow", label: "After release — small label (default: Out now)", kind: "text" },
      { key: "out_now_meta", label: "After release — date/format line (optional override)", kind: "text" },
      { key: "out_now_banner", label: "After release — celebratory banner text (links allowed)", kind: "textarea", rows: 2 },
      { key: "about_label", label: "Compact mode — collapsed 'More about…' row label (blank = hidden)", kind: "text" },
      { key: "about_body", label: "More about — story paragraphs (blank line between; links allowed)", kind: "textarea", rows: 8 },
      { key: "about_quote", label: "More about — pull quote", kind: "textarea", rows: 2 },
      { key: "about_quote_by", label: "More about — quote attribution", kind: "text" },
      { key: "about_facts_label", label: "More about — facts card heading (e.g. Release facts)", kind: "text" },
      { key: "about_facts", label: "More about — facts", kind: "link-list", itemFields: [
        { key: "when", label: "Small label (e.g. Out, Format, Cause)", kind: "text" },
        { key: "text", label: "Text (links allowed)", kind: "textarea" },
        { key: "link", label: "Source link (optional)", kind: "text" },
      ] },
      { key: "eyebrow", label: "Small label above the title (e.g. New single · out Sept 19)", kind: "text" },
      { key: "cover", label: "Cover art", kind: "image" },
      { key: "title", label: "Release title", kind: "text" },
      { key: "artist", label: "Artist", kind: "text" },
      { key: "release_date", label: "Release date line (e.g. Out Saturday, September 19, 2026)", kind: "text" },
      { key: "format", label: "Format (e.g. Single · 3:55 · 24-bit)", kind: "text" },
      { key: "dedication", label: "Dedication / one-line quote (optional)", kind: "textarea", rows: 2 },
      { key: "blurb", label: "About the release — [text](url) for links", kind: "textarea", rows: 5 },
      { key: "embed_url", label: "Player embed — paste the full <iframe> code or a Bandcamp / SoundCloud / YouTube link", kind: "textarea", rows: 3 },
      { key: "embed_height", label: "Player height in px (Bandcamp slim = 42, standard = 120, with tracklist = 400)", kind: "number" },
      { key: "buy_url", label: "Buy / stream link (main button)", kind: "text" },
      { key: "buy_label", label: "Main button label", kind: "text" },
      { key: "links", label: "Platform pills (Spotify, Apple Music, YouTube…)", kind: "link-list", itemFields: [
        { key: "label", label: "Label", kind: "text" },
        { key: "url", label: "URL", kind: "text" },
        { key: "icon", label: "Icon name (lucide: Music, Headphones, Youtube, Disc3, Radio)", kind: "text" },
      ] },
      { key: "cause_title", label: "Cause box — small heading (e.g. Where the money goes)", kind: "text" },
      { key: "cause_note", label: "Cause box — text (links allowed)", kind: "textarea", rows: 3 },
      { key: "show_share", label: "Show the Share button", kind: "boolean" },
      { key: "accent", label: "Accent color", kind: "color" },
      { key: "background", label: "Background color", kind: "color" },
    ],
    Render: ReleaseSpotlightBlock,
  },
  link_hub: {
    label: "Link Hub — Follow / listen / press tiles",
    hint: "A grid of platform, social and press links with icons. Icon names are from lucide (Instagram, Facebook, Youtube, Twitter, Music, Headphones, Newspaper, Mic, Globe, Mail).",
    icon: "Link",
    defaultConfig: { intro: "", columns: 4, accent: "#2E5C55", links: [] },
    fields: [
      { key: "intro", label: "Intro line (optional)", kind: "text" },
      { key: "columns", label: "Columns (2, 3 or 4)", kind: "number" },
      { key: "anchor", label: "Link anchor (e.g. follow → #follow)", kind: "text" },
      { key: "collapsed_show", label: "Collapse: show only the first N links until 'Show all' (0 = all)", kind: "number" },
      { key: "accent", label: "Default tile color", kind: "color" },
      { key: "links", label: "Links", kind: "link-list", itemFields: [
        { key: "label", label: "Label (e.g. Instagram)", kind: "text" },
        { key: "sublabel", label: "Small line (e.g. @dainnormanmusic)", kind: "text" },
        { key: "url", label: "URL or path", kind: "text" },
        { key: "icon", label: "Icon name (lucide)", kind: "text" },
        { key: "color", label: "Tile color (optional)", kind: "color" },
      ] },
    ],
    Render: LinkHubBlock,
  },
  story_chapter: {
    label: "Story Chapter — Year · theme · narrative · photos · proof links",
    hint: "One chapter of a long-form story. Body supports **bold**, *italic*, [link text](url) and [Hear it](moment:key) for an inline radio button. Start a paragraph with [+ Title] to make it an expandable section (the lines after the title are hidden until tapped). A paragraph [photos: url \"caption\" | url \"caption\" | url \"caption\"] lays photos out inline — first one big on the left, the rest stacked on the right. Start a paragraph with [#my-anchor] to give it a link target (#my-anchor). Each media row can be a photo, a video (MP4/MOV) or an audio clip (MP3/M4A/WAV) — upload straight from here. Photos open a lightbox; videos and audio get inline players. Proof pills link to sources.",
    icon: "BookOpen",
    defaultConfig: { year: "", theme: "", body: "", layout: "media-below", columns: 3, accent: "#B8860B", media: [], proofs: [], proofs_label: "", quote: "", quote_by: "", city_events: [], city_label: "" },
    fields: [
      { key: "year", label: "Year (big label on the left, e.g. 2014 — use | to force a line break, e.g. y2112:|In a Nutshell)", kind: "text" },
      { key: "cta_label", label: "Highlighted button after the body (label, optional)", kind: "text" },
      { key: "cta_url", label: "Highlighted button link (e.g. #learning-model or /page)", kind: "text" },
      { key: "theme", label: "Theme line under the year (e.g. The Soil of Earth)", kind: "text" },
      { key: "voice_label", label: "Hear-it heading in the floating column (default: the site-wide 'Hear it' text — Admin → Radio → Button text)", kind: "text" },
      { key: "moments", label: "Hear-it buttons in the floating column — point at radio moments by stable key (recorded clips for this section appear here automatically via 'Record a clip')", kind: "link-list", itemFields: [
        { key: "key", label: "Moment key (set on a chapter in Admin → Radio, e.g. spiking-overview)", kind: "text" },
        { key: "label", label: "Button text (blank = site default + the chapter title)", kind: "text" },
        { key: "note", label: "Note under the button — what they'll hear (optional)", kind: "textarea", rows: 2 },
      ] },
      { key: "anchor", label: "Link anchor for this chapter (optional, e.g. giving-shop → #giving-shop)", kind: "text" },
      { key: "badge_image", label: "Top badge — supporter/sponsor logo (optional, shows at the top of the chapter)", kind: "image" },
      { key: "badge_text", label: "Top badge — phrase beside the logo (links allowed)", kind: "textarea", rows: 2 },
      { key: "badge_link", label: "Top badge — link the logo/badge opens (optional URL)", kind: "text" },
      { key: "body", label: "Narrative — blank line between paragraphs; [text](url) for links", kind: "textarea", rows: 10 },
      { key: "quote", label: "Pull quote (optional)", kind: "textarea", rows: 2 },
      { key: "quote_by", label: "Quote attribution", kind: "text" },
      { key: "accent", label: "Accent color", kind: "color" },
      { key: "link_color", label: "Link color (body links, proof pills, sources — defaults to accent)", kind: "color" },
      { key: "city_position", label: "Around-the-city column position", kind: "select", options: ["right", "left"] },
      { key: "media_above_count", label: "media-above: how many images go above the text (1st full-width, rest side by side)", kind: "number" },
      { key: "no_rail", label: "No floating left column — title label, lead image and side media are hidden; the body runs full width", kind: "boolean" },
      { key: "single_column", label: "Keep the text in one column (long bodies otherwise split into two on wide screens)", kind: "boolean" },
      { key: "media_float_count", label: "media-above: how many of the next images float right with text wrapping around them (set the above-count to 0 to float the first image)", kind: "number" },
      { key: "pair_with_next", label: "Pair with the next block (render side by side on large screens)", kind: "boolean" },
      { key: "dark", label: "Dark band (distinct section, e.g. The Giving Shop)", kind: "boolean" },
      { key: "background", label: "Dark band color (optional, e.g. #0A1626 to match a release spotlight)", kind: "color" },
      { key: "city_enabled", label: "Show the 'Around the city' editor placeholder for this chapter", kind: "boolean" },
      { key: "lead_image", label: "Lead image (full-width in the left column, under the year label)", kind: "image" },
      { key: "lead_caption", label: "Lead image caption (optional)", kind: "textarea" },
      { key: "side_media", label: "Floating column extras — full-size images (click to enlarge) or uploaded video clips, stacked under the lead image", kind: "link-list", itemFields: [
        { key: "title", label: "Title above (optional)", kind: "text" },
        { key: "image", label: "Image (or video cover)", kind: "image" },
        { key: "video_url", label: "Video URL (/api/uploads/videos/....mp4 — optional)", kind: "text" },
        { key: "audio_url", label: "Audio URL (/api/uploads/audio/....mp3 — optional, play-in-place)", kind: "text" },
        { key: "caption", label: "Caption (links allowed)", kind: "textarea" },
        { key: "link", label: "External link (optional)", kind: "text" },
        { key: "link_label", label: "Link label (default: Read the article →)", kind: "text" },
      ] },
      { key: "shows_label", label: "Show posters — heading in the floating left column (default: Our Shows)", kind: "text" },
      { key: "shows", label: "Show posters — music show posters & handbills, stacked under the lead image in the floating left column", kind: "link-list", itemFields: [
        { key: "image", label: "Poster", kind: "image" },
        { key: "caption", label: "Caption (links allowed)", kind: "textarea" },
        { key: "embed_url", label: "Player embed URL (Bandcamp/Spotify iframe src — optional, shows under the poster)", kind: "text" },
        { key: "embed_height", label: "Player height px (default 120)", kind: "text" },
      ] },
      { key: "collapse_after", label: "Read more: show only the first N paragraphs until tapped (0 = all)", kind: "number" },
      { key: "collapse_all", label: "Read more: hide the whole body (only title + quip show) until tapped", kind: "boolean" },
      { key: "read_more_label", label: "Read-more button label", kind: "text" },
      { key: "media_when_collapsed", label: "Read more: keep the photos visible while the text is collapsed", kind: "boolean" },
      { key: "read_more_style", label: "Read-more button style", kind: "select", options: ["outline", "prominent"] },
      { key: "promo_image", label: "Promo image (e.g. a shop logo, shown after the body)", kind: "image" },
      { key: "promo_text", label: "Promo text (under the image)", kind: "textarea", rows: 3 },
      { key: "promo_button", label: "Promo button label", kind: "text" },
      { key: "promo_url", label: "Promo link", kind: "text" },
      { key: "layout", label: "Media placement (media-above = first image full-width above the text, rest below)", kind: "select", options: ["media-below", "media-right", "media-above"] },
      { key: "columns", label: "Media columns (1–4)", kind: "number" },
      { key: "media_show", label: "Media: show only the first N items until 'Show all' (0 = all)", kind: "number" },
      { key: "media_more_label", label: "Media 'show all' button label", kind: "text" },
      { key: "media", label: "Media — photos, videos & audio for this year. Order here = slideshow order (use the ▲▼ arrows); the caption is the slide text.", kind: "link-list", itemFields: [
        { key: "image", label: "Photo (or a cover image for the audio clip below)", kind: "image" },
        { key: "video", label: "Video (MP4/MOV, up to 200MB)", kind: "video" },
        { key: "audio", label: "Audio clip (MP3/M4A/WAV, up to 40MB)", kind: "audio" },
        { key: "label", label: "Corner label (e.g. Poster, Program, Press)", kind: "text" },
        { key: "focus", label: "Crop focus (optional, e.g. 30% 40% — where the face is)", kind: "text" },
        { key: "fit", label: "Image fit (cover crops to 4:5 · contain letterboxes · natural = full image, own height)", kind: "select", options: ["cover", "contain", "natural"] },
        { key: "caption", label: "Caption (links allowed)", kind: "textarea" },
        { key: "link", label: "Open this link instead of enlarging (optional, photos only)", kind: "text" },
      ] },
      { key: "proofs_label", label: "Proof row label (default: Proof & sources)", kind: "text" },
      { key: "proofs", label: "Proof & source links", kind: "link-list", itemFields: [
        { key: "label", label: "Label", kind: "text" },
        { key: "url", label: "URL, path or #anchor", kind: "text" },
      ] },
      { key: "city_label", label: "Right-column label (default: Around the city)", kind: "text" },
      { key: "city_position", label: "Where the list shows", kind: "select", options: ["sidebar", "inline"] },
      { key: "gallery_label", label: "Photo gallery — small heading (optional)", kind: "text" },
      { key: "gallery_images", label: "Photo gallery — thumbnails with one shared caption (shown after the media)", kind: "image-list" },
      { key: "gallery_caption", label: "Photo gallery — the single caption under it", kind: "textarea", rows: 3 },
      { key: "city_events", label: "Around the city — significant events that year (right column, shows our impact in context)", kind: "link-list", itemFields: [
        { key: "when", label: "When (e.g. Nov 13, Summer)", kind: "text" },
        { key: "text", label: "What happened (links allowed)", kind: "textarea" },
        { key: "link", label: "Source link (optional)", kind: "text" },
      ] },
      { key: "side_audio", label: "Audio clips in the floating left column (paste an uploaded audio URL, e.g. /api/uploads/audio/....mp3)", kind: "link-list", itemFields: [
        { key: "title", label: "Title", kind: "text" },
        { key: "audio_url", label: "Audio URL (uploaded mp3)", kind: "text" },
        { key: "embed_url", label: "Embed URL instead (YouTube /embed/… or Bandcamp EmbeddedPlayer) — used if no audio", kind: "text" },
        { key: "embed_height", label: "Embed height px (default 120)", kind: "number" },
        { key: "image", label: "Cover image URL (optional)", kind: "text" },
        { key: "caption", label: "Caption (optional)", kind: "textarea" },
        { key: "link", label: "Link (artist page path or URL, optional)", kind: "text" },
        { key: "link_label", label: "Link label", kind: "text" },
      ] },
    ],
    Render: StoryChapterBlock,
  },
  artist_shows: {
    label: "Artist Shows — Upcoming dates from their tour/calendar + subscribe + owner 'drop a flyer'",
    hint: "Live list of the artist's upcoming shows (what they publish on /my-tour). Visitors can subscribe (.ics); the verified owner sees 'Add shows — just drop a flyer'.",
    icon: "CalendarDays",
    defaultConfig: { artist_slug: "", intro: "", accent: "#2E5C55", limit: 12, show_past: false, full_link: "", full_link_label: "", empty_text: "" },
    fields: [
      { key: "artist_slug", label: "Artist slug (e.g. guilded-lilly)", kind: "text" },
      { key: "intro", label: "Intro line (optional)", kind: "text" },
      { key: "anchor", label: "Link anchor (e.g. shows)", kind: "text" },
      { key: "accent", label: "Accent color", kind: "color" },
      { key: "limit", label: "Max shows listed", kind: "number" },
      { key: "show_past", label: "Show a 'Recently' line with the last 3 shows", kind: "boolean" },
      { key: "full_link", label: "Link to the full tour page (e.g. /artists/guilded-lilly)", kind: "text" },
      { key: "full_link_label", label: "Link label", kind: "text" },
      { key: "empty_text", label: "Text when no dates are posted", kind: "text" },
    ],
    Render: ArtistShowsBlock,
  },
  tour_stops: {
    label: "Tour Stops — Dates, venues, map + share buttons",
    hint: "For a person or band on tour. Each stop gets its own Share button; lat/lng adds a map pin.",
    icon: "Route",
    defaultConfig: { intro: "", stops: [] },
    fields: [
      { key: "intro", label: "Intro line (optional)", kind: "text" },
      { key: "stops", label: "Stops", kind: "link-list", itemFields: [
        { key: "date", label: "Date (YYYY-MM-DD)", kind: "text" },
        { key: "time", label: "Time (e.g. 11am–1pm)", kind: "text" },
        { key: "venue", label: "Venue / business / org", kind: "text" },
        { key: "address", label: "Address", kind: "text" },
        { key: "city", label: "City", kind: "text" },
        { key: "note", label: "What happens here", kind: "textarea" },
        { key: "badge", label: "Badge (e.g. Keynote, EXAMPLE)", kind: "text" },
        { key: "flyer", label: "Flyer image", kind: "image" },
        { key: "link", label: "Details link (optional)", kind: "text" },
        { key: "lat", label: "Latitude (optional)", kind: "text" },
        { key: "lng", label: "Longitude (optional)", kind: "text" },
      ] },
    ],
    Render: TourStopsBlock,
  },
  poster_wall: {
    label: "Poster Wall — Community flyers & photos (Porchfest-style)",
    hint: "Admins approve posters from the Admin Inbox / Porchfest tools. Uses this page's slug unless overridden.",
    icon: "Images",
    defaultConfig: { event_slug: "", empty_text: "" },
    fields: [
      { key: "event_slug", label: "Poster wall slug (blank = this page)", kind: "text" },
      { key: "empty_text", label: "Empty-state text", kind: "text" },
    ],
    Render: PosterWallBlock,
  },
  feature_cards: {
    label: "Cards — Programs, stats, ways to help or a numbered list",
    hint: "A grid of cards. Style 'stats' shows big numbers; 'list' is a numbered reading list (e.g. founding documents). Icon names are from lucide (e.g. Bus, Truck, Heart). Tip: the block's 'Collapsed by default' switch makes it a click-to-expand section.",
    icon: "LayoutGrid",
    defaultConfig: { style: "cards", columns: 3, cards: [] },
    fields: [
      { key: "intro", label: "Intro paragraph (optional; [text](url) for links)", kind: "textarea", rows: 3 },
      { key: "logo", label: "Logo shown above the cards (optional, e.g. the Starflower)", kind: "image" },
      { key: "anchor", label: "Link anchor (optional, e.g. wishlist → #wishlist)", kind: "text" },
      { key: "collapsed_show", label: "Collapse: show only the first N cards until 'Show all' (0 = show all)", kind: "number" },
      { key: "locked_closed", label: "Lock closed (title + quip only, e.g. 'Section coming soon')", kind: "boolean" },
      { key: "style", label: "Style", kind: "select", options: ["cards", "stats", "list"] },
      { key: "columns", label: "Columns (2, 3 or 4)", kind: "text" },
      { key: "cards", label: "Cards", kind: "link-list", itemFields: [
        { key: "title", label: "Title (or the big number for stats)", kind: "text" },
        { key: "image", label: "Image shown at the top of the card (optional)", kind: "image" },
        { key: "tag", label: "Small label above title", kind: "text" },
        { key: "blurb", label: "Text", kind: "textarea" },
        { key: "points", label: "Bullet points (one per line)", kind: "textarea" },
        { key: "icon", label: "Icon name (lucide)", kind: "text" },
        { key: "color", label: "Accent color", kind: "color" },
        { key: "link", label: "Link (path or URL)", kind: "text" },
        { key: "link_label", label: "Link label", kind: "text" },
      ] },
    ],
    Render: FeatureCardsBlock,
  },
  timeline: {
    label: "Timeline — Story milestones",
    hint: "Vertical timeline: when / title / detail.",
    icon: "History",
    defaultConfig: { items: [] },
    fields: [
      { key: "items", label: "Milestones", kind: "link-list", itemFields: [
        { key: "when", label: "When (e.g. 2023, Dec 2025)", kind: "text" },
        { key: "title", label: "Title", kind: "text" },
        { key: "detail", label: "Detail", kind: "textarea" },
      ] },
    ],
    Render: TimelineBlock,
  },
  north_star: {
    label: "North Star (legacy — renders nothing)",
    hint: "Replaced by the North Star guide card that appears automatically under the hero. Safe to delete.",
    icon: "Star",
    defaultConfig: { note: "" },
    fields: [{ key: "note", label: "Extra note (optional)", kind: "text" }],
    Render: NorthStarBlock,
  },
  cta: {
    label: "Call to Action — Button card",
    hint: "One prominent button on a colored card.",
    icon: "MousePointerClick",
    defaultConfig: { button_label: "Learn more", button_route: "/", accent: "#E76F51", body: "" },
    fields: [
      { key: "body", label: "Card body", kind: "textarea", rows: 3 },
      { key: "anchor", label: "Link anchor (optional, e.g. fund-the-ark → #fund-the-ark)", kind: "text" },
      { key: "button_position", label: "Button position", kind: "select", options: ["default", "bottom-center"] },
      { key: "layout", label: "Layout", kind: "select", options: ["default", "image-left", "image-top", "image-bottom"] },
      { key: "headline", label: "Big headline under the image (image-top layout only)", kind: "text" },
      { key: "headline_url", label: "Headline link (optional URL or path)", kind: "text" },
      { key: "plain", label: "No card background (blend into the page)", kind: "boolean" },
      { key: "image_full", label: "Image spans the full width (image-bottom layout)", kind: "boolean" },
      { key: "pills_label", label: "Pill row label (e.g. Jump to a year)", kind: "text" },
      { key: "pills", label: "Pill links (shown under the body)", kind: "link-list", itemFields: [
        { key: "label", label: "Label", kind: "text" }, { key: "url", label: "Anchor or URL (e.g. #year-2012)", kind: "text" },
      ] },
      { key: "button_label", label: "Button label", kind: "text" },
      { key: "button_route", label: "Button destination (URL or path)", kind: "route" },
      { key: "accent", label: "Accent color", kind: "color" },
      { key: "image", label: "Flyer / photo (optional — sits where the button was; the button moves under the text)", kind: "image" },
    ],
    Render: CtaBlock,
  },
  sponsors: {
    label: "Sponsors / Partners — Logo wall",
    hint: "Grid of partner logos with optional links.",
    icon: "Handshake",
    defaultConfig: { sponsors: [] },
    fields: [
      { key: "sponsors", label: "Sponsors", kind: "link-list", itemFields: [
        { key: "name", label: "Name", kind: "text" },
        { key: "logo", label: "Logo image", kind: "image" },
        { key: "url", label: "Link URL", kind: "text" },
      ]},
    ],
    Render: SponsorsBlock,
  },
  nav_card_embed: {
    label: "Nav Card — Link + inline embed",
    hint: "Big pill card that jumps to another page and optionally shows it inline in an iframe when expanded.",
    icon: "PanelTop",
    defaultConfig: {
      route: "/", emoji: "✨", accent_color: "#2E5C55",
      coming_soon: false, show_embed: true, open_tab_label: "For best nav experience open here",
      cta_label: "", cta_route: "",
    },
    fields: [
      { key: "route", label: "Route (e.g. /events)", kind: "route" },
      { key: "emoji", label: "Emoji", kind: "text" },
      { key: "accent_color", label: "Accent color", kind: "color" },
      { key: "coming_soon", label: "Show \"Coming Soon\" state", kind: "boolean" },
      { key: "show_embed", label: "Show inline iframe when expanded", kind: "boolean" },
      { key: "open_tab_label", label: "Open-in-tab link label", kind: "text" },
      { key: "cta_label", label: "Extra CTA label (optional)", kind: "text" },
      { key: "cta_route", label: "Extra CTA route (optional)", kind: "route" },
    ],
    Render: NavCardEmbedBlock,
  },
  announcement_ticker: {
    label: "Announcement Ticker — Scheduled banners",
    hint: "One block, many announcements. Each row has its own start/end window — the block auto-shows whichever entries are currently in-window. Perfect for a campaign leading up to a festival: volunteer call → vendor call → parade lineup → day-of alert.",
    icon: "Megaphone",
    defaultConfig: { announcements: [] },
    fields: [
      { key: "announcements", label: "Scheduled announcements (each shows during its own window)", kind: "link-list", itemFields: [
        { key: "id", label: "Slug (unique — used for dismiss memory)", kind: "text" },
        { key: "message", label: "Message", kind: "textarea", rows: 2 },
        { key: "priority", label: "Priority (info / warning / urgent)", kind: "select", options: ["info", "warning", "urgent"] },
        { key: "accent", label: "Custom accent color (optional, overrides priority)", kind: "color" },
        { key: "start_at", label: "Start (blank = show immediately)", kind: "datetime" },
        { key: "end_at", label: "End (blank = show forever)", kind: "datetime" },
        { key: "cta_label", label: "CTA button label (optional)", kind: "text" },
        { key: "cta_route", label: "CTA link (optional)", kind: "route" },
        { key: "dismissable", label: "Let visitors dismiss it", kind: "boolean" },
      ], templates: [
        { label: "📣 Volunteer call",
          entry: { message: "Volunteers wanted! Help us make this year's event extraordinary — sign up now.",
                   priority: "info", cta_label: "Sign up", cta_route: "", dismissable: true } },
        { label: "🎪 Vendor / performer call",
          entry: { message: "Vendor & performer applications are open. Apply by [date].",
                   priority: "info", cta_label: "Apply", cta_route: "", dismissable: true } },
        { label: "🎭 Parade lineup announced",
          entry: { message: "The lineup is here! See who's marching, playing, and cheering this year.",
                   priority: "info", cta_label: "See lineup", cta_route: "", dismissable: false } },
        { label: "🎟️ Tickets / RSVP open",
          entry: { message: "Tickets and RSVPs are open — grab yours before they're gone.",
                   priority: "info", cta_label: "Get tickets", cta_route: "", dismissable: true } },
        { label: "👕 Merch drop",
          entry: { message: "Fresh merch just dropped — support the event and rep the community.",
                   priority: "info", cta_label: "Shop merch", cta_route: "", dismissable: true } },
        { label: "⚠️ Weather / day-of alert",
          entry: { message: "⚠️ Weather update: [details]. Please check back for updates.",
                   priority: "urgent", cta_label: "", cta_route: "", dismissable: false } },
        { label: "📍 Day-of schedule change",
          entry: { message: "Heads up — schedule change today: [details].",
                   priority: "warning", cta_label: "", cta_route: "", dismissable: false } },
        { label: "💚 Thank-you (post-event)",
          entry: { message: "Thank you to every volunteer, band, sponsor, and neighbor who made this happen. See you next year.",
                   priority: "info", cta_label: "", cta_route: "", dismissable: true } },
      ]},
    ],
    Render: AnnouncementTickerBlock,
  },
  north_star_guide: {
    label: "North Star Guide — Drink & Food Spiking",
    hint: "The consistent safety card: title + lede, MORE expands to symptoms, what to do, symptom checker, caveats, sources.",
    icon: "ShieldAlert",
    defaultConfig: { eyebrow: "Drink & Food Spiking — Protecting People Professionally", title: "Have a REAL good time.", lede: "Desire is natural — **drugs that induce desire violate your consent.** Protect yourself & friends: **know the signs** of food & drink spiking & get to **know the symptoms** of the drugs used **in case something happens**. Eg: [New & deadly: Cychlorphine —](https://www.king5.com/article/news/nation-world/what-is-cychlorphine-synthetic-opioid-linked-overdose-deaths/507-f1936135-8e5a-408e-bd0d-96f56a74aa5f){#B91C1C} [KING 5 NEWS](https://www.king5.com/article/news/nation-world/what-is-cychlorphine-synthetic-opioid-linked-overdose-deaths/507-f1936135-8e5a-408e-bd0d-96f56a74aa5f)", more_label: "MORE", guide_route: "/abuse#signs", guide_label: "RECOGNIZE ABUSE TACTICS — Understanding Abuse →", open_by_default: false },
    fields: [
      { key: "eyebrow", label: "Eyebrow", kind: "text" },
      { key: "title", label: "Title", kind: "text" },
      { key: "lede", label: "Lede (the one-paragraph message)", kind: "textarea", rows: 3 },
      { key: "more_label", label: "Expand button label", kind: "text" },
      { key: "guide_route", label: "Full guide link", kind: "text" },
      { key: "guide_label", label: "Full guide button label", kind: "text" },
      { key: "open_by_default", label: "Start expanded", kind: "boolean" },
      { key: "compact", label: "Compact — title row with buttons beside it, lede clamped to one line (default ON; untick for the big card)", kind: "boolean" },
      { key: "hide_share", label: "Hide the Share this card button (default ON)", kind: "boolean" },
    ],
    Render: NorthStarGuideBlock,
  },
  guide_card: {
    label: "Guide card — collapsible sections + share image (any topic)",
    hint: "The North Star card's look for any issue: eyebrow, title, lede, MORE expands your sections (each can start collapsed with a teaser). Share button renders a 1080² card.",
    icon: "ShieldAlert",
    defaultConfig: { accent: "#1D4ED8", more_label: "MORE", open_by_default: false, share_enabled: true, sections: [] },
    fields: [
      { key: "eyebrow", label: "Eyebrow (small line above the title)", kind: "text" },
      { key: "title", label: "Title (falls back to the section title)", kind: "text" },
      { key: "lede", label: "Lede — the one-paragraph message (**bold**, [links](url))", kind: "textarea", rows: 3 },
      { key: "accent", label: "Accent color", kind: "color" },
      { key: "more_label", label: "Expand button label", kind: "text" },
      { key: "open_by_default", label: "Start expanded", kind: "boolean" },
      { key: "anchor", label: "Link anchor (e.g. guide → #guide)", kind: "text" },
      { key: "scan", label: "Scan mode — paragraphs & bullets clamp to a line or two with (cont.) (the Cannabis-guide look)", kind: "boolean" },
      { key: "sections", label: "Sections (▲▼ to reorder · Add blank row to add a section)", kind: "link-list", itemFields: [
        { key: "title", label: "Section title", kind: "text" },
        { key: "kind", label: "Style", kind: "select", options: ["text", "bullets", "numbered", "quotes", "alert", "stat", "places"] },
        { key: "stat", label: "Big number (stat style only, e.g. 1 in 10)", kind: "text" },
        { key: "body", label: "Body — one paragraph / bullet / quote per line (▲▼ above the box reorders lines). Quotes style: line 1 = intro, next lines = quotes, a blank line then closing text. **bold**, [links](url)", kind: "lines", rows: 5 },
        { key: "collapsed", label: "Collapsed by default", kind: "boolean" },
        { key: "teaser", label: "Summary shown while collapsed (optional)", kind: "textarea", rows: 2 },
        { key: "show_label", label: "Expand button label (default: Show)", kind: "text" },
        { key: "hidden", label: "Hide this section", kind: "boolean" },
      ] },
      { key: "cta_url", label: "Inline button — link (path, optional; e.g. /community-detox)", kind: "text" },
      { key: "cta_label", label: "Inline button — label", kind: "text" },
      { key: "cta_every", label: "Inline button — repeat after every N sections", kind: "number" },
      { key: "cta_color", label: "Inline button — color (default: accent)", kind: "color" },
      { key: "link_url", label: "Bottom button link (path or URL, optional)", kind: "text" },
      { key: "link_label", label: "Bottom button label", kind: "text" },
      { key: "disclaimer", label: "Disclaimer line (links to /disclaimer, optional)", kind: "text" },
      { key: "share_enabled", label: "Show the Share-this-card button", kind: "boolean" },
      { key: "share_label", label: "Share button label", kind: "text" },
      { key: "share_footer_line", label: "Share card — footer headline (required for the share button, e.g. 1 in 4 renters face …)", kind: "text" },
      { key: "share_link_line", label: "Share card — link line (default: Learn at THE WALL: LOOKWASHINGTON.com/<this page>)", kind: "text" },
      { key: "share_tell_label", label: "Share card — small label above the key line (default: Remember)", kind: "text" },
      { key: "share_tell_body", label: "Share card — the one key line", kind: "textarea", rows: 2 },
      { key: "share_alert_title", label: "Share card — red alert title (default: first 'alert' section)", kind: "text" },
      { key: "share_alert_body", label: "Share card — red alert body", kind: "textarea", rows: 2 },
      { key: "share_brand", label: "Share card — brand line top-right (default: THE WALL · <title>)", kind: "text" },
    ],
    Render: GuideCardBlock,
  },
  featured_artists: {
    label: "Featured Artists — curated players",
    hint: "Short strip of hand-picked artists with a player and link each.",
    icon: "Music2",
    defaultConfig: { eyebrow: "Listen while you read · Artists from our community", title: "", body: "", artists: [] },
    fields: [
      { key: "eyebrow", label: "Eyebrow", kind: "text" },
      { key: "title", label: "Title (optional)", kind: "text" },
      { key: "body", label: "Intro (optional)", kind: "textarea", rows: 2 },
      { key: "artists", label: "Artists", kind: "link-list", itemFields: [
        { key: "name", label: "Name", kind: "text" },
        { key: "tagline", label: "Tagline", kind: "text" },
        { key: "image", label: "Photo URL", kind: "text" },
        { key: "audio_url", label: "Audio URL (uploaded mp3)", kind: "text" },
        { key: "embed_url", label: "Embed URL (Bandcamp/Spotify — used if no audio)", kind: "text" },
        { key: "link", label: "Artist page path (e.g. /sunny-loudin)", kind: "text" },
        { key: "link_label", label: "Link label", kind: "text" },
      ] },
    ],
    Render: FeaturedArtistsBlock,
  },
  suggest_changes: {
    label: "Suggest changes — private review form",
    hint: "Visitors send corrections; stored for admin review, never auto-published.",
    icon: "Send",
    defaultConfig: { title: "Suggest changes to this page", body: "Corrections, additions, photos, credits — send them here. Nothing you submit is published automatically; our editor reviews every note before anything changes." },
    fields: [
      { key: "title", label: "Title", kind: "text" },
      { key: "body", label: "Body", kind: "textarea", rows: 3 },
    ],
    Render: SuggestChangesBlock,
  },
  safety_plan: {
    label: "Safety & Exit Plan — interactive, device-only",
    hint: "An interactive DV safety/exit plan: trusted people + code word, go-bag & document checklists, tech-safety order, hotlines. Saves only in the visitor's browser; includes a Quick-exit button. Based on the National DV Hotline plan.",
    icon: "ShieldCheck",
    defaultConfig: { accent: "#9D174D", title: "Build your safety & exit plan", anchor: "safety-plan", open_by_default: false },
    fields: [
      { key: "title", label: "Title", kind: "text" },
      { key: "accent", label: "Accent color", kind: "color" },
      { key: "anchor", label: "Link anchor (e.g. safety-plan → #safety-plan)", kind: "text" },
      { key: "open_by_default", label: "Start expanded", kind: "boolean" },
    ],
    Render: SafetyPlanBlock,
  },
  ...HOME_BLOCK_TYPES,
};

export const BLOCK_TYPE_KEYS = Object.keys(BLOCK_REGISTRY);
